<template>
  <main
    class="login-page min-h-screen w-full relative text-gray-900 dark:text-gray-100"
    style="font-family: 'Inter', sans-serif;"
  >
    <div class="login-page__content min-h-screen flex items-center justify-center px-4 py-10 relative z-10">
      <div class="w-full max-w-md">
        <div class="bg-white border border-gray-200 rounded-xl shadow-sm px-10 py-10 sm:p-8 dark:border-white/10 dark:bg-white/5">
          <div class="flex items-center justify-center gap-2.5 mb-6">
            <img class="h-9 w-auto" src="../../assets/logo.png" alt="" />
          </div>

          <div class="text-center mb-6">
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              Enter your current password and choose a new one.
            </p>
          </div>

          <form @submit.prevent="submit">
            <div class="space-y-5">
              <div class="relative">
                <CTailwindInput
                  id="cp-old"
                  label="Current password"
                  :value="oldPassword"
                  :type="showOldPassword ? 'text' : 'password'"
                  name="old-password"
                  autocomplete="current-password"
                  placeholder="Current password"
                  class="pr-10"
                  @input="oldPassword = $event"
                />
                <button
                  class="absolute right-0 top-6 flex h-10 w-10 items-center justify-center text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                  type="button"
                  aria-label="Toggle password visibility"
                  @click="showOldPassword = !showOldPassword"
                >
                  <i :class="showOldPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'" />
                </button>
              </div>

              <div class="relative">
                <CTailwindInput
                  id="cp-new"
                  label="New password"
                  :value="password"
                  :type="showPassword1 ? 'text' : 'password'"
                  name="new-password"
                  autocomplete="new-password"
                  placeholder="New password"
                  class="pr-10 mt-5"
                  @input="password = $event"
                />
                <button
                  class="absolute right-0 top-6 flex h-10 w-10 items-center justify-center text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                  type="button"
                  aria-label="Toggle password visibility"
                  @click="showPassword1 = !showPassword1"
                >
                  <i :class="showPassword1 ? 'mdi mdi-eye-off' : 'mdi mdi-eye'" />
                </button>
              </div>

              <div class="relative">
                <CTailwindInput
                  id="cp-confirm"
                  label="Confirm new password"
                  :value="password2"
                  :type="showPassword2 ? 'text' : 'password'"
                  name="confirm-password"
                  autocomplete="new-password"
                  placeholder="Confirm new password"
                  class="pr-10 mt-5"
                  @input="password2 = $event"
                />
                <button
                  class="absolute right-0 top-6 flex h-10 w-10 items-center justify-center text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                  type="button"
                  aria-label="Toggle password visibility"
                  @click="showPassword2 = !showPassword2"
                >
                  <i :class="showPassword2 ? 'mdi mdi-eye-off' : 'mdi mdi-eye'" />
                </button>
              </div>
            </div>

            <p v-if="matchError" class="mt-3 text-sm text-red-600 dark:text-red-400">
              New passwords must match and be at least 8 characters.
            </p>

            <c-tailwind-btn
              type="submit"
              variant="primary"
              class="mt-6 flex min-h-[2.75rem] w-full items-center justify-center py-2.5"
              :disabled="!isFormValid || loading"
            >
              <span v-if="loading" class="inline-flex items-center gap-2">
                <span
                  class="h-4 w-4 shrink-0 rounded-full border-2 border-white/35 border-t-white animate-spin"
                />
                Updating…
              </span>
              <span v-else>Change password</span>
            </c-tailwind-btn>
          </form>

          <p class="text-center text-sm text-gray-600 dark:text-gray-400 mt-6">
            <button
              type="button"
              class="font-medium text-gray-900 hover:underline dark:text-gray-100"
              @click="goBack"
            >
              Back to app
            </button>
          </p>

          <div class="mt-8 pt-6 border-t border-gray-100 text-xs text-gray-500 text-center dark:border-white/10 dark:text-gray-400">
            © 2026 MetalNx. All rights reserved. Contact: info@metalnx.com
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { changePassword } from '@/api/users'
import CTailwindInput from '@/components/Vuetify/CTailwindInput.vue'
import CTailwindBtn from '@/components/Vuetify/CTailwindBtn.vue'

export default {
  name: 'ChangePasswordPage',
  components: {
    CTailwindInput,
    CTailwindBtn,
  },
  data() {
    return {
      showOldPassword: false,
      showPassword1: false,
      showPassword2: false,
      oldPassword: '',
      password: '',
      password2: '',
      loading: false,
      matchError: false,
    }
  },
  computed: {
    userId() {
      return this.$store.state.UserId
    },
    isFormValid() {
      return Boolean(
        this.oldPassword &&
          this.password &&
          this.password2 &&
          this.password.length >= 8 &&
          this.password2.length >= 8 &&
          this.password === this.password2
      )
    },
  },
  methods: {
    goBack() {
      if (this.$store.state.isStaff === true) {
        this.$router.push('/admin')
      } else {
        this.$router.push('/user/home')
      }
    },
    submit() {
      if (!this.isFormValid || this.loading) {
        this.matchError = !this.isFormValid
        return
      }
      this.matchError = false
      this.loading = true
      const _this = this
      changePassword(
        this.userId,
        this.oldPassword,
        this.password,
        this.password2,
        function () {
          _this.oldPassword = ''
          _this.password = ''
          _this.password2 = ''
          _this.loading = false
        },
        function (err) {
          _this.loading = false
          if (err.status == 400) {
            let msg = ''
            for (const k of Object.keys(err.data)) {
              msg += k + ':' + err.data[k][0]
            }
            _this.$root.notify.show({ message: msg, type: 'error' })
          } else {
            _this.$root.notify.show({ message: 'Internal Error!', type: 'error' })
          }
        },
        'Password changed successfully'
      )
    },
  },
}
</script>

<style scoped>
.login-page::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-color: hsla(165, 0%, 100%, 1);
  background-repeat: no-repeat;
  background-size: 100% 100%;
  background-position: center;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 962 962' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E"),
    radial-gradient(
      circle at 20% 100%,
      hsla(197, 77%, 74%, 0.35) 3.1210986267166043%,
      transparent 40%
    ),
    radial-gradient(
      circle at 40% 100%,
      hsla(147, 77%, 74%, 0.35) 3.1210986267166043%,
      transparent 40%
    ),
    radial-gradient(
      circle at 80% 100%,
      hsla(22, 0%, 100%, 0.35) 3.1210986267166043%,
      transparent 40%
    ),
    radial-gradient(
      circle at 100% 100%,
      hsla(171, 70%, 35%, 0.57) 3%,
      transparent 40%
    );
  background-blend-mode: overlay, normal, normal, normal, normal;
}

html.dark .login-page {
  background-color: hsl(218 24% 8%);
}

html.dark .login-page::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-color: hsl(218 24% 8%);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-blend-mode: normal;
  background-image: radial-gradient(
    ellipse 140% 85% at 50% 100%,
    hsl(180 22% 14%) 0%,
    hsl(218 26% 9%) 42%,
    hsl(220 20% 7%) 100%
  );
}
</style>
